import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteDefaultQueryStateFromTable } from 'components/table/utlil';
import {
  closeModal,
  initialQueryState,
  openModal,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';

import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Institution } from '../type';

import { trimValue } from 'helpers';
import { useBoolean, useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { debounce, isUndefined } from 'lodash';

import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

export const useInstitutionsController = () => {
  const navigationTo = useNavigate();

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.INSTITUTIONS]);

  const [institutionData, setInstitutionData] = useState<Institution[]>([]);

  const total = useNumber(0);
  const isLoading = useBoolean(true);
  const loadingAPI = useBoolean(false);

  const searchValue = useString('');

  const institutionIdSelected = useString('');

  useEffect(() => {
    updateQueryStateTable(TABLE_KEY.INSTITUTIONS, initialQueryState);

    return () => {
      updateQueryStateTable(TABLE_KEY.INSTITUTIONS, initialQueryState);
    };
  }, []);

  useEffect(() => {
    if (queryState) {
      getListInstitution();
    }
  }, [JSON.stringify(queryState)]);

  const getListInstitution = async (search: string = '') => {
    isLoading.setValue(true);

    const result: {
      items: Institution[];
      total: number;
    } = await apiCall({
      url: '/institutions',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        ...deleteDefaultQueryStateFromTable(queryState),
        search: search,
      },
    });

    loadingAPI.setValue(false);

    if (result?.items) {
      setInstitutionData(result?.items ?? []);
    }

    if (!isUndefined(result?.total)) {
      total.setValue(result.total);
    }

    isLoading.setValue(false);
  };

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      getListInstitution(trimValue(newSearch));
    }, 500),
    [],
  );

  const goToAddPage = () => {
    navigationTo(`${PATH.INSTITUTIONS}/${PATH.ADD_INSTITUTIONS}`);
  };

  const onClickAction = async (key: 'delete' | 'update-status', institutionId: string) => {
    switch (key) {
      case 'delete':
        institutionIdSelected.setValue(institutionId);
        openModal(MODAL_KEY.DELETE_INSTITUTION);
        break;

      case 'update-status':
        await apiCall({
          url: `/institutions/${institutionId}/status`,
          method: 'PATCH',
          isLoading: true,
          showError: true,
        });

        showToastMessage('success', `Updated institution's status successfully`);

        getListInstitution(searchValue.value);

        break;

      default:
        break;
    }
  };

  const onSubmitDeleteInstitution = async () => {
    if (institutionIdSelected.value) {
      await apiCall({
        url: `/institutions/${institutionIdSelected.value}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });
      closeModal(MODAL_KEY.DELETE_INSTITUTION);

      showToastMessage('success', 'Removed Institution successfully!');

      if (queryState.page === 1) {
        getListInstitution(searchValue.value);

        return;
      }

      updateQueryStateTable(TABLE_KEY.INSTITUTIONS, {
        ...queryState,
        page: 1,
      });
    }
  };

  const onClickRow = (row: any) => {
    const original: Institution = row.original;

    if (original?.id) {
      navigationTo(PATH.INSTITUTION_DETAIL.replace(':institutionId', original.id));
    }
  };

  return {
    institutionData,
    total,
    handleUpdated: getListInstitution,
    queryState,
    searchValue,
    onChangeSearchValue,
    goToAddPage,
    onClickAction,
    onSubmitDeleteInstitution,
    onClickRow,
  };
};
